import React, {useState} from "react";
import {Chip, MenuItem, Select, SelectChangeEvent, Stack} from "@mui/material";
import {DateRange} from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import CalendarDialog from "@/app2/pages/TeamProgressPage/components/DateRangeCalendar/CalendarDialog";
import {
	applyLeaderboardDateFilterUpdate,
	type LeaderboardDateFilterState,
	type LeaderboardDateRangeType
} from "./leaderboardDateRange";

export type LeaderboardDateFiltersProps = {
	value: LeaderboardDateFilterState;
	onChange: (next: LeaderboardDateFilterState) => void;
};

export const LeaderboardDateFilters: React.FC<LeaderboardDateFiltersProps> = ({
	value,
	onChange
}) => {
	const {t} = useSSTranslation();
	const [openDialog, setOpenDialog] = useState(false);
	const {dateRangeType, dateFrom, dateTo} = value;
	const isCustomDateRange = dateRangeType === "customDateRange";

	const handleChangeDateRangeFilter = (event: SelectChangeEvent<LeaderboardDateRangeType>) => {
		const selectedOption = event.target.value as LeaderboardDateRangeType;
		if (selectedOption === "customDateRange") {
			setOpenDialog(true);
			return;
		}
		onChange(applyLeaderboardDateFilterUpdate(value, {dateRangeType: selectedOption}));
	};

	const handleChangeDateRange = (range: DateRange<Date>) => {
		onChange(
			applyLeaderboardDateFilterUpdate(value, {
				dateRangeType: "customDateRange",
				dateFrom: dayjs(range[0]).format("YYYY-MM-DD"),
				dateTo: dayjs(range[1]).format("YYYY-MM-DD")
			})
		);
	};

	const handleClearDateRange = () => {
		onChange(applyLeaderboardDateFilterUpdate(value, {dateRangeType: "lastMonth"}));
	};

	return (
		<Stack direction={{xs: "column", md: "row"}}>
			{!isCustomDateRange ? (
				<Select onChange={handleChangeDateRangeFilter} value={dateRangeType}>
					<MenuItem value="lastWeek">
						{t("team_progress_filter_last_week", "Last Week")}
					</MenuItem>
					<MenuItem value="lastMonth">
						{t("team_progress_filter_last_month", "This Month")}
					</MenuItem>
					<MenuItem value="lastYear">
						{t("team_progress_filter_last_year", "Last Year")}
					</MenuItem>
					<MenuItem value="customDateRange">
						{t("team_progress_filter_custom_date_range", "Custom Date Range")}
					</MenuItem>
				</Select>
			) : (
				<Chip
					variant="outlined"
					color="primary"
					label={`${dateFrom ? dayjs(dateFrom).format("MM/DD/YYYY") : ""} - ${dateTo ? dayjs(dateTo).format("MM/DD/YYYY") : ""}`}
					onDelete={handleClearDateRange}
					sx={(theme) => ({
						"& .MuiChip-label": {
							color: theme.palette.common.white
						}
					})}
				/>
			)}
			{openDialog ? (
				<CalendarDialog
					onAcceptDateRange={handleChangeDateRange}
					onClose={() => setOpenDialog(false)}
				/>
			) : null}
		</Stack>
	);
};
